import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  clearAuth,
  devLogin,
  enrollFirstPasskey,
  getToken,
  getUser,
  login,
  passkeyLogin,
  PasskeyEnrollError,
  registerAccount,
  requestPasswordReset,
  resetPassword,
  verifyEmail,
} from '@/lib/auth';
import { isPasskeySupported } from '@/lib/passkey';
import { decodeEnrollmentTokenClaims } from '@/lib/enrollmentToken';
import { recoverSeityByEmail } from '@/api/seityOnboarding';
import { resolvePostLoginLanding, type PostLoginLanding } from '@/api/billing';
import {
  buildExternalLoginRedirectUrl,
  externalLoginVertical,
  isAllowedExternalLoginReturnTo,
  resolveExternalCustomerTenant,
  resolveExternalLoginVertical,
} from '@/lib/externalLoginReturn';

// ── Visual language ─────────────────────────────────────────────────────────
// Scoped, self-contained stylesheet for the Seitiate+ sign-in surface (Claude
// Design restyle). Every selector is nested under `.slp` so it cannot leak into
// the rest of the dashboard's `.sp-*` design system. This changes ONLY the
// presentation — all auth logic, endpoints, modes, and handlers are untouched.
const SLP_CSS = `
.slp {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: #0a0c10;
  color: #c8d0dc;
  font-family: 'Space Grotesk', ui-sans-serif, system-ui, -apple-system, sans-serif;
}
.slp-grain {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}
.slp-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  background: #0e1015;
  border: 1px solid rgba(57, 255, 20, 0.08);
  border-radius: 12px;
  padding: 44px 40px 36px;
}
.slp-brand {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 28px;
  font-family: 'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 1.55rem;
  letter-spacing: -0.01em;
}
.slp-brand-mark { color: #39ff14; font-weight: 700; }
.slp-brand-name { color: #c8d0dc; font-weight: 500; }
.slp-section-label {
  margin-bottom: 20px;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #555;
}
.slp-heading {
  margin-bottom: 16px;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #c8d0dc;
}
.slp-label {
  display: block;
  margin-bottom: 8px;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #888;
}
.slp-input {
  width: 100%;
  background: #161a22;
  border: 1px solid rgba(57, 255, 20, 0.14);
  border-radius: 5px;
  color: #c8d0dc;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 0.95rem;
  padding: 13px 14px;
  transition: border-color 150ms ease;
}
.slp-input::placeholder { color: #4a4f57; }
.slp-input--password { padding-right: 46px; }
.slp-input--error { border-color: rgba(239, 68, 68, 0.5); }
.slp-input:focus { outline: none; border-color: #39ff14; }
.slp-input:-webkit-autofill,
.slp-input:-webkit-autofill:hover,
.slp-input:-webkit-autofill:focus {
  -webkit-text-fill-color: #c8d0dc;
  -webkit-box-shadow: 0 0 0 1000px #161a22 inset;
  box-shadow: 0 0 0 1000px #161a22 inset;
  caret-color: #c8d0dc;
}
.slp-field { position: relative; }
.slp-toggle {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  color: #888;
  transition: color 150ms ease;
}
.slp-toggle:hover { color: #c8d0dc; }
.slp-hint {
  margin-top: 6px;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 0.72rem;
  color: #4a4f57;
}
.slp-msg {
  margin-top: 12px;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 0.8rem;
  line-height: 1.5;
}
.slp-msg--error { color: #ef4444; }
.slp-msg--error::before { content: "✕ "; }
.slp-msg--note { color: #888; }
.slp-btn-primary {
  width: 100%;
  padding: 14px;
  background: #39ff14;
  color: #0a0c10;
  border: 0;
  border-radius: 5px;
  font-family: 'Space Grotesk', ui-sans-serif, system-ui, sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 150ms ease, transform 150ms ease;
}
.slp-btn-primary:hover:not(:disabled) { filter: brightness(1.1); transform: translateY(-1px); }
.slp-btn-primary:disabled { opacity: 0.55; cursor: default; }
.slp-btn-ghost {
  width: 100%;
  padding: 13px;
  background: transparent;
  color: #c8d0dc;
  border: 1px solid rgba(57, 255, 20, 0.14);
  border-radius: 5px;
  font-family: 'Space Grotesk', ui-sans-serif, system-ui, sans-serif;
  font-size: 0.92rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 150ms ease, color 150ms ease, background 150ms ease;
}
.slp-btn-ghost:hover:not(:disabled) {
  border-color: #39ff14;
  color: #39ff14;
  background: rgba(57, 255, 20, 0.06);
}
.slp-btn-ghost:disabled { opacity: 0.55; cursor: default; }
.slp-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 18px 0;
}
.slp-divider::before,
.slp-divider::after { content: ""; flex: 1; height: 1px; }
.slp-divider::before { background: linear-gradient(90deg, transparent, rgba(57, 255, 20, 0.12)); }
.slp-divider::after { background: linear-gradient(90deg, rgba(57, 255, 20, 0.12), transparent); }
.slp-divider span {
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 0.68rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #555;
}
.slp-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 16px;
  margin-top: 24px;
}
.slp-link {
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: 'Space Grotesk', ui-sans-serif, system-ui, sans-serif;
  font-size: 0.82rem;
  color: #888;
  transition: color 150ms ease;
}
.slp-link:hover { color: #39ff14; }
.slp-link-unit {
  display: inline-flex;
  align-items: baseline;
  gap: 16px;
}
.slp-dot { color: #33383f; user-select: none; }
.slp-info {
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #12151b;
  border: 1px solid rgba(57, 255, 20, 0.10);
  border-radius: 6px;
  font-size: 0.75rem;
  line-height: 1.5;
  color: #888;
}
.slp-info a { color: #39ff14; }
.slp-info-strong { color: #c8d0dc; }
.slp-foot {
  margin-top: 24px;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 0.74rem;
  line-height: 1.6;
  color: #4a4f57;
}
`;

// Standard sign-in footnote copy (Claude Design). Applied consistently across
// every mode of the page.
const FOOTNOTE = 'You are being logged into a Seitiate compliant website.';

// Renders a wrapping row of secondary link-buttons, joined by an aria-hidden
// "·" separator. Falsy entries (mode-gated affordances) are dropped, so the
// row only ever shows the links that apply to the current context.
function LinkRow({ links }: { links: ReactNode[] }) {
  const shown = links.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <div className="slp-links">
      {shown.map((el, i) => (
        <span className="slp-link-unit" key={i}>
          {i > 0 && (
            <span className="slp-dot" aria-hidden="true">
              ·
            </span>
          )}
          {el}
        </span>
      ))}
    </div>
  );
}

function userFacingLoginMessage(message?: string): string | undefined {
  if (!message) return undefined;
  return /invalid authentication token/i.test(message)
    ? 'Your session expired. Sign in again.'
    : message;
}

async function redirectToExternalReturn(
  returnTo: string,
  signedVerticalId?: string | null,
): Promise<void> {
  const token = getToken();
  const user = getUser();
  if (!token || !user) return;
  // Prefer the vertical_id WE signed (the enrollment token, verified server-side)
  // over origin-derivation. Every configured vertical now has a unique origin
  // (hennipenny moved to its own app.hennipenny.com in Model B), so origin-derivation
  // already resolves each one; the signed id stays authoritative for any future shared
  // workspace origin where one hostname serves more than one vertical.
  const vertical = resolveExternalLoginVertical(returnTo, signedVerticalId) ?? 'external';
  // EXPLICIT tenant selection at the vertical door (HENNIPENNY-LAB-ACTIVATION-1):
  // resolve WHICH of the user's memberships holds this vertical's billing
  // admission — the engine's owner-first default must never decide this (a lab
  // MEMBER who owns a personal tenant would silently land in the personal
  // tenant). No admitted membership → honest rejection, no redirect.
  const activeTenantId = await resolveExternalCustomerTenant(vertical);
  window.location.assign(
    buildExternalLoginRedirectUrl(returnTo, token, user, vertical, activeTenantId),
  );
}

const VERTICAL_DISPLAY_NAMES: Record<string, string> = {
  sensarra: 'Sensarra',
  'evidence-foundry': 'Evidence Foundry',
  hennipenny: 'HenniPenny',
  sakme: 'Sakme',
};

// Customer-home origins are often authenticated applications, not their
// reviewed-access forms. Keep explicit public application destinations for
// verticals where those differ so "New here?" can never loop back to login.
const VERTICAL_ACCESS_URLS: Record<string, string> = {
  'evidence-foundry': 'https://evidencefoundry.com/#apply',
  hennipenny: 'https://hennipenny.com/#apply',
  // sensarra's landing anchor is #early-access (research-hub LandingPage.tsx
  // section id="early-access", the EarlyAccessForm), not #apply. Without this
  // entry a newcomer at the app.sensarra.com door would be bounced back to the
  // door origin itself — the login loop this map exists to prevent.
  sensarra: 'https://sensarra.com/#early-access',
};

// Verticals whose login must NOT expose the Seitiate+ platform brand (white-label
// / OEM partners — their branding is the partner's call). They still get the
// honest one-account / apply message, just without the platform name.
const WHITE_LABEL_VERTICALS = new Set<string>(['hennipenny', 'sensarra']);

// Static, per-vertical platform banner shown on every seitiate+ customer login.
// SAFE by construction: the same copy shows for everyone arriving at a given
// vertical's login — it never inspects the typed email, so it cannot confirm
// whether any specific account exists (no enumeration oracle, unlike a per-email
// "you're already a member" check). It carries the honest "one account across the
// platform — sign in with your existing email, or apply" message.
function PlatformBanner({ verticalId, returnTo }: { verticalId: string | null; returnTo: string | null }) {
  const name = (verticalId && VERTICAL_DISPLAY_NAMES[verticalId]) || 'this product';
  const whiteLabel = verticalId !== null && WHITE_LABEL_VERTICALS.has(verticalId);
  let accessHref = verticalId ? VERTICAL_ACCESS_URLS[verticalId] ?? null : null;
  try {
    accessHref = accessHref ?? (returnTo ? new URL(returnTo).origin : null);
  } catch {
    accessHref = null;
  }
  const applyCta = accessHref ? (
    <a href={accessHref}>request access from {name}</a>
  ) : (
    <>request access from {name}</>
  );
  return (
    <div className="slp-info">
      {whiteLabel ? (
        <>
          Already have an account? Sign in with your email below — one account works across our
          products. New here? You can {applyCta}.
        </>
      ) : (
        <>
          {name} runs on the <span className="slp-info-strong">Seitiate+</span> platform.
          Already have a Seitiate+ account? Sign in with your email below — your subscription and
          credits work across every Seitiate+ product. New here? You can {applyCta}.
        </>
      )}
    </div>
  );
}

function removeSensitiveQueryParam(name: 'verify_token' | 'reset_token'): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(name)) return;
  url.searchParams.delete(name);
  const search = url.searchParams.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${search ? `?${search}` : ''}${url.hash}`,
  );
}

// The Seitiate+ login. No Firebase Google/GitHub buttons, no Sakme branding.
// Customer password auth calls POST /api/v1/auth/login; the explicit operator
// recovery mode alone calls /api/v1/auth/dev-login. Both store the engine JWT.
//
// External hosted verticals pass ?return_to=https://…/auth/callback;
// after login we hand the JWT back in the URL fragment (paired with
// research-hub /auth/callback consumer). The allowlisted return origin derives
// the vertical key used for attribution; the vertical is not trusted from the
// browser alone.

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState = location.state as { from?: string; message?: string } | null;
  const routeMessage = userFacingLoginMessage(locationState?.message);
  const externalReturnTo = useMemo(() => {
    const raw = searchParams.get('return_to');
    return isAllowedExternalLoginReturnTo(raw) ? raw!.trim() : null;
  }, [searchParams]);
  // First-run passkey enrollment (P-7): the "set up your passkey" email deep-link
  // arrives as /login?enroll=<one-time-token>. Presence flips this page into the
  // enrollment screen.
  const enrollToken = useMemo(() => {
    const raw = searchParams.get('enroll');
    return raw && raw.trim() ? raw.trim() : null;
  }, [searchParams]);
  // The enrollment token binds the approved email (and its signed tenant/vertical/
  // return target). The email lives ONLY inside the token, not in the URL query, so
  // we decode it (unverified — UI pre-fill only) to seed the passphrase door's email
  // field. Identity is still enforced server-side by register+login.
  const enrollClaims = useMemo(() => decodeEnrollmentTokenClaims(enrollToken), [enrollToken]);
  // Email-verification deep-link (?verify_token=…) and password-reset deep-link
  // (?reset_token=…) — same "query param flips the page into a sub-mode" pattern
  // as ?enroll= above. Distinct param names because both can only ever appear
  // one-at-a-time from a real email, but sharing a single `?token=` name would
  // make an old bookmarked/forwarded verify link silently misfire as a
  // password-reset attempt (and vice versa) if both landed on this page.
  const verifyToken = useMemo(() => {
    const raw = searchParams.get('verify_token');
    return raw && raw.trim() ? raw.trim() : null;
  }, [searchParams]);
  const resetTokenParam = useMemo(() => {
    const raw = searchParams.get('reset_token');
    return raw && raw.trim() ? raw.trim() : null;
  }, [searchParams]);
  // "Use a different account" escape (breaks the passkey/session lock-in): ?switch=1
  // forces a fresh login form even when a session is cached, so a returning user on a
  // shared machine is never auto-redirected into whoever is already signed in.
  const forceLogin = useMemo(() => {
    const raw = searchParams.get('switch');
    return raw === '1' || raw === 'true';
  }, [searchParams]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [directorKey, setDirectorKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operatorPasswordMode, setOperatorPasswordMode] = useState(false);
  const isExternalCustomerLogin = externalReturnTo !== null;
  const passwordLoginLabel = operatorPasswordMode ? 'Operator password override' : 'Sign in';

  // ── Door-2 passphrase recovery (identity-login SSOT Stage 0) ──────────────
  // A permanent, pre-auth path: a locked-out user resets their seity passphrase
  // with their show-once recovery key BEFORE logging in. Does not touch login.
  const [mode, setMode] = useState<
    'login' | 'recover' | 'enroll' | 'register' | 'forgot' | 'reset' | 'verify'
  >(
    enrollToken ? 'enroll' : verifyToken ? 'verify' : resetTokenParam ? 'reset' : 'login',
  );
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [enrollDone, setEnrollDone] = useState(false);
  // ── Enroll screen, PRIMARY door: choose a passphrase (the shipped per-user
  // email+password path, worded "passphrase" per the 2026-07-13 ruling). The
  // enrollment token is structurally first-passkey-only, so the passphrase door
  // routes through register+login for the token-bound email rather than through
  // the token. Email pre-filled from the decoded token, editable.
  const [enrollPassEmail, setEnrollPassEmail] = useState(() => enrollClaims.email ?? '');
  const [enrollPassphrase, setEnrollPassphrase] = useState('');
  const [enrollPassShow, setEnrollPassShow] = useState(false);
  const [enrollPassBusy, setEnrollPassBusy] = useState(false);
  const [enrollPassError, setEnrollPassError] = useState<string | null>(null);
  const [enrollPassNotice, setEnrollPassNotice] = useState<string | null>(null);
  const [enrollPassSent, setEnrollPassSent] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverKey, setRecoverKey] = useState('');
  const [recoverNewPass, setRecoverNewPass] = useState('');
  const [recoverBusy, setRecoverBusy] = useState(false);
  const [recoverError, setRecoverError] = useState<string | null>(null);
  const [recoverNotice, setRecoverNotice] = useState<string | null>(null);

  // A notice shown on the LOGIN form itself (distinct from `error`) — used to
  // hand a message across a mode switch, e.g. "account created, sign in
  // below" after registration, or "password reset, sign in with your new
  // password" after a reset. Cleared whenever a fresh login attempt starts.
  const [notice, setNotice] = useState<string | null>(null);

  // After a BARE-login success (no return_to), the customer verticals this user
  // is admitted to (across ALL memberships). When set, the page shows a
  // "Continue to <vertical> →" interstitial instead of dumping a lab member on
  // the S+ platform dashboard. Null = not resolved / not applicable (operators
  // and non-customers fall through to today's dashboard behavior).
  const [postLoginLanding, setPostLoginLanding] = useState<PostLoginLanding | null>(null);

  // Whether a session already existed at PAGE-LOAD time. The auto-redirect guard
  // below fires only for that case (a returning, already-signed-in visit). It must
  // NOT read the live token, or it would fire the instant a FRESH login sets the
  // token — racing ahead of goAfterLogin and dumping a just-logged-in lab member on
  // the dashboard before their vertical landing resolves. Fresh-login routing is
  // owned entirely by goAfterLogin.
  const sessionAtMount = useRef<boolean | null>(null);
  if (sessionAtMount.current === null) {
    sessionAtMount.current = Boolean(getToken() && getUser());
  }

  // ── Create-account (identity-login SSOT §0; POST /auth/register) ─────────
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerShowPassword, setRegisterShowPassword] = useState(false);
  const [registerBusy, setRegisterBusy] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerDone, setRegisterDone] = useState(false);
  const [continueAfterRegisterBusy, setContinueAfterRegisterBusy] = useState(false);

  // ── Forgot password request (POST /auth/request-password-reset) ──────────
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotNotice, setForgotNotice] = useState<string | null>(null);

  // ── Reset password consume (POST /auth/reset-password) ───────────────────
  const [resetToken, setResetToken] = useState(resetTokenParam ?? '');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetShowPassword, setResetShowPassword] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // ── Verify email consume, URL-token-driven (POST /auth/verify-email) ─────
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyDone, setVerifyDone] = useState(false);
  // First-passphrase step for an approved person (the engine answers 400 "Password is
  // required" for an account that has no password yet). The token and the passphrase live
  // in component state only: never in a URL, local storage or a log.
  const [verifyTokenHeld, setVerifyTokenHeld] = useState<string | null>(null);
  const [verifyNeedsPassphrase, setVerifyNeedsPassphrase] = useState(false);
  const [verifyPassphrase, setVerifyPassphrase] = useState('');
  const [verifyPassBusy, setVerifyPassBusy] = useState(false);
  const [verifyPassError, setVerifyPassError] = useState<string | null>(null);

  const openRecover = () => {
    setRecoverEmail(email);
    setRecoverKey('');
    setRecoverNewPass('');
    setRecoverError(null);
    setRecoverNotice(null);
    setMode('recover');
  };

  const backToLogin = () => {
    setRecoverError(null);
    setRecoverNotice(null);
    setMode('login');
  };

  const openRegister = () => {
    setRegisterName('');
    setRegisterEmail(email);
    setRegisterPassword('');
    setRegisterShowPassword(false);
    setRegisterError(null);
    setRegisterDone(false);
    setNotice(null);
    setMode('register');
  };

  const backToLoginFromRegister = () => {
    setRegisterError(null);
    setMode('login');
  };

  const onRegisterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setRegisterBusy(true);
    setRegisterError(null);
    try {
      await registerAccount(registerEmail, registerPassword, registerName);
      setRegisterDone(true);
    } catch (err) {
      setRegisterError(err instanceof Error ? err.message : 'Could not create account.');
    } finally {
      setRegisterBusy(false);
    }
  };

  // Registration issues no session token (RegisterResponse has none) — sign
  // in now with the same credentials so the user lands logged in, per the
  // ruled login. Explicit click (not auto-fired) so the "check your email"
  // notice above it is actually read, not instantly navigated past.
  const onContinueAfterRegister = async () => {
    setContinueAfterRegisterBusy(true);
    try {
      await login(registerEmail, registerPassword);
      await goAfterLogin();
    } catch {
      // Vanishingly unlikely (registration just created this exact
      // credential) but must not trap the user in an error state — hand
      // them back to plain sign-in with the notice instead of a dead end.
      setEmail(registerEmail);
      setNotice('Account created. Check your email to verify it, then sign in below.');
      setMode('login');
    } finally {
      setContinueAfterRegisterBusy(false);
    }
  };

  const openForgotPassword = () => {
    setForgotEmail(email);
    setForgotError(null);
    setForgotNotice(null);
    setNotice(null);
    setMode('forgot');
  };

  const backToLoginFromForgot = () => {
    setForgotError(null);
    setForgotNotice(null);
    setMode('login');
  };

  const onForgotSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setForgotBusy(true);
    setForgotError(null);
    setForgotNotice(null);
    try {
      await requestPasswordReset(forgotEmail);
      // Always the same copy, success or not — the backend never reveals
      // whether the email has an account (no enumeration oracle here either).
      setForgotNotice('If that email has an account, we sent a code to reset the password. Check your inbox.');
    } catch (err) {
      setForgotError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setForgotBusy(false);
    }
  };

  const openReset = () => {
    setResetToken(resetTokenParam ?? '');
    setResetNewPassword('');
    setResetShowPassword(false);
    setResetError(null);
    setNotice(null);
    setMode('reset');
  };

  const backToLoginFromReset = () => {
    setResetError(null);
    setMode('login');
  };

  const onResetSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setResetBusy(true);
    setResetError(null);
    try {
      await resetPassword(resetToken, resetNewPassword);
      setResetNewPassword('');
      setEmail(forgotEmail || email);
      setNotice('Your password has been reset. Sign in with your new password.');
      setMode('login');
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'That reset code is invalid or has expired.');
    } finally {
      setResetBusy(false);
    }
  };

  // Fully sign out of the cached session and reload a clean login form (preserving
  // the vertical's return_to). This is the escape hatch when the browser has a stale
  // passkey/session and would otherwise gate the user out of signing in as someone
  // else on a shared machine.
  const switchAccount = () => {
    clearAuth();
    setEmail('');
    setPassword('');
    setDirectorKey('');
    setShowPassword(false);
    setOperatorPasswordMode(false);
    setError(null);
    setMode('login');
    const params = new URLSearchParams();
    if (externalReturnTo) params.set('return_to', externalReturnTo);
    params.set('switch', '1');
    navigate({ pathname: '/login', search: `?${params.toString()}` }, { replace: true });
  };

  const attemptExternalRedirect = async (
    returnTo: string,
    signedVerticalId?: string | null,
  ): Promise<boolean> => {
    try {
      await redirectToExternalReturn(returnTo, signedVerticalId);
      return true;
    } catch (err) {
      clearAuth();
      setError(err instanceof Error ? err.message : 'Customer access is not ready.');
      return false;
    }
  };

  const onRecoverSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setRecoverBusy(true);
    setRecoverError(null);
    setRecoverNotice(null);
    try {
      const res = await recoverSeityByEmail(recoverEmail, recoverKey, recoverNewPass);
      // The engine response is intentionally generic (no account enumeration):
      // a success here means "request accepted", not "recovery succeeded".
      setRecoverKey('');
      setRecoverNewPass('');
      setRecoverNotice(
        res?.message ??
          'If that account exists and the recovery key is correct, the seity passphrase has been reset. Sign in and unlock with your new passphrase.',
      );
    } catch (err) {
      setRecoverError(err instanceof Error ? err.message : 'Recovery failed. Please try again.');
    } finally {
      setRecoverBusy(false);
    }
  };

  // Auto-redirect a cached session onward — UNLESS the user is here to enroll a new
  // passkey (?enroll=), verify email (?verify_token=) or reset a password
  // (?reset_token=), or explicitly asked to switch accounts (?switch=1); all of
  // those must win over "you're already signed in".
  useEffect(() => {
    if (
      externalReturnTo &&
      getToken() &&
      getUser() &&
      !enrollToken &&
      !forceLogin &&
      !verifyToken &&
      !resetTokenParam
    ) {
      void attemptExternalRedirect(externalReturnTo);
    }
  }, [externalReturnTo, enrollToken, forceLogin, verifyToken, resetTokenParam]);

  // Arriving with ?switch=1 drops any cached session so the form is truly fresh.
  useEffect(() => {
    if (forceLogin && (getToken() || getUser())) {
      clearAuth();
    }
  }, [forceLogin]);

  // Email-verification tokens are single-use — consume it once, on arrival,
  // regardless of whether a session is already cached (a signed-in user
  // clicking an old verify link should still see the result).
  useEffect(() => {
    if (!verifyToken) return;
    removeSensitiveQueryParam('verify_token');
    let cancelled = false;
    setVerifyTokenHeld(verifyToken);
    setVerifyBusy(true);
    setVerifyError(null);
    verifyEmail(verifyToken)
      .then(() => {
        if (!cancelled) setVerifyDone(true);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const status = (err as { status?: unknown } | null)?.status;
          if (status === 400 && err instanceof Error && /password is required/i.test(err.message)) {
            setVerifyNeedsPassphrase(true);
            return;
          }
          setVerifyError(
            err instanceof Error ? err.message : 'That verification link is invalid or has expired.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setVerifyBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [verifyToken]);

  // Keep the reset token in component state after reading it, but immediately
  // remove the bearer-like secret from browser history and copied URLs.
  useEffect(() => {
    if (resetTokenParam) removeSensitiveQueryParam('reset_token');
  }, [resetTokenParam]);

  if (
    sessionAtMount.current &&
    getToken() &&
    getUser() &&
    !enrollToken &&
    !forceLogin &&
    !verifyToken &&
    !resetTokenParam
  ) {
    if (externalReturnTo) {
      return (
        <div className="slp">
          <style>{SLP_CSS}</style>
          <div className="slp-grain" aria-hidden="true" />
          <div className="slp-card" style={{ textAlign: 'center' }}>
            <div className="slp-brand" style={{ justifyContent: 'center' }}>
              <span className="slp-brand-mark">S+</span>
              <span className="slp-brand-name">Seitiate+</span>
            </div>
            <div className="slp-msg slp-msg--note" style={{ marginTop: 0 }}>
              Returning to {externalLoginVertical(externalReturnTo) ?? 'your workspace'}…
            </div>
            <LinkRow
              links={[
                <button key="switch" type="button" className="slp-link" onClick={switchAccount}>
                  Not you? Use a different account
                </button>,
              ]}
            />
          </div>
        </div>
      );
    }
    return <Navigate to="/dashboard" replace />;
  }

  const goAfterLogin = async (
    returnToOverride?: string | null,
    signedVerticalId?: string | null,
  ) => {
    const returnTo = returnToOverride === undefined ? externalReturnTo : returnToOverride;
    if (returnTo) {
      await attemptExternalRedirect(returnTo, signedVerticalId);
      return;
    }
    // Bare login (no return_to). Before dropping the user on the S+ platform
    // dashboard, resolve which customer verticals they're admitted to (across ALL
    // memberships — the same cross-membership rule the vertical doors use). A lab
    // member should be sent to their vertical, not the platform's Research
    // connections page. Never block login on this: any failure falls through to
    // today's dashboard behavior. Operators/Directors resolve is_operator:true
    // with no verticals, so they are never redirected or diverted.
    if (locationState?.from && locationState.from !== '/login') {
      navigate(locationState.from, { replace: true });
      return;
    }
    try {
      const landing = await resolvePostLoginLanding();
      if (landing && !landing.is_operator) {
        if (landing.redirect_url) {
          window.location.assign(landing.redirect_url);
          return;
        }
        if (landing.verticals.length > 0) {
          setPostLoginLanding(landing);
          return;
        }
      }
    } catch {
      // Resolution unavailable — fall through to the dashboard.
    }
    navigate('/dashboard', { replace: true });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    // "Operator password override" is a deliberately separate, explicitly
    // chosen mode for internal/team access to a customer-facing (external
    // return_to) login — it is the shared team credential (dev-login).
    const useOperatorCredential = isExternalCustomerLogin && operatorPasswordMode;
    try {
      if (useOperatorCredential) {
        await devLogin(email, password, directorKey.trim() || undefined);
        // Operator recovery is platform administration, not customer
        // impersonation. A dev-password token must never be handed to a
        // hosted vertical callback.
        await goAfterLogin(null);
      } else {
        // /auth/login owns the interim compatibility gate. The backend may
        // accept the shared team credential only while this account has no
        // per-user password hash; the browser never retries through dev-login.
        await login(email, password);
        await goAfterLogin();
      }
    } catch (err) {
      // The engine 404s /auth/dev-login when the deployment carries no
      // operator registry, or when the Director key is missing or wrong
      // (same 404 body either way). Duck-typed status check (not instanceof)
      // so partially-mocked auth modules stay safe.
      const status = (err as { status?: unknown } | null)?.status;
      if (useOperatorCredential && status === 404) {
        setError(
          'The engine refused the operator override: this deployment has no operator registry, or the Director key is missing or wrong.',
        );
      } else {
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    } finally {
      if (useOperatorCredential) setDirectorKey('');
      setBusy(false);
    }
  };

  const onPasskeyLogin = async () => {
    setBusy(true);
    setError(null);
    try {
      await passkeyLogin();
      await goAfterLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  const onEnroll = async () => {
    if (!enrollToken) return;
    setEnrollBusy(true);
    setEnrollError(null);
    try {
      const enrollment = await enrollFirstPasskey(enrollToken, 'This device');
      const signedReturnTo = isAllowedExternalLoginReturnTo(enrollment.return_to)
        ? enrollment.return_to!.trim()
        : null;
      // The enrollment token binds a SIGNED vertical_id (verified server-side and
      // echoed here). Carry it into the return so the billing gate checks the vertical
      // the customer actually holds — authoritative over origin-derivation for any
      // shared workspace origin (each vertical has its own origin today, incl.
      // hennipenny on app.hennipenny.com, but the token stays the source of truth).
      const signedVerticalId = enrollment.vertical_id ?? null;
      // Enrollment persists the credential but issues NO session — sign in now
      // (a second passkey tap) to get one, then route onward.
      setEnrollDone(true);
      await passkeyLogin();
      await goAfterLogin(signedReturnTo, signedVerticalId);
    } catch (err) {
      if (err instanceof PasskeyEnrollError && err.status === 409) {
        // Link already used / this account already has a passkey — sign in instead.
        setError('You already have a passkey on this account. Sign in below.');
        setMode('login');
      } else if (err instanceof PasskeyEnrollError && err.status === 401) {
        setEnrollError(
          'This setup link has expired. Ask your operator to re-send your access email.',
        );
      } else {
        setEnrollError(
          err instanceof Error ? err.message : 'Passkey setup failed. Please try again.',
        );
      }
    } finally {
      setEnrollBusy(false);
    }
  };

  // PRIMARY enrollment door: set a passphrase, then sign in and land in the
  // workspace. The enrollment token is structurally first-passkey-only, so the
  // passphrase door routes through the shipped register+login path for the
  // token-bound email rather than through the token itself.
  const onVerifyPassphrase = async (e: FormEvent) => {
    e.preventDefault();
    if (!verifyTokenHeld) return;
    setVerifyPassBusy(true);
    setVerifyPassError(null);
    try {
      const verified = await verifyEmail(verifyTokenHeld, verifyPassphrase);
      // The passphrase is set and the email is verified. Sign the person in with what they
      // just chose, so they do not have to type it a third time; then drop it from state.
      const chosen = verifyPassphrase;
      setVerifyPassphrase('');
      setVerifyNeedsPassphrase(false);
      setVerifyDone(true);
      // Never silently replace a session that already belongs to someone else: only sign in
      // automatically when no one is signed in here, or it is the same account.
      const existing = getUser();
      const sameOrNobody =
        !getToken() ||
        !existing ||
        (existing.email ?? '').toLowerCase() === verified.email.toLowerCase();
      if (verified.email && sameOrNobody) {
        try {
          await login(verified.email, chosen);
          await goAfterLogin();
        } catch {
          // Verified, but the automatic sign-in did not complete: the page already says
          // "You can sign in now", which stays true.
        }
      }
    } catch (err) {
      setVerifyPassError(
        err instanceof Error ? err.message : 'That verification link is invalid or has expired.',
      );
    } finally {
      setVerifyPassBusy(false);
    }
  };

  const onEnrollPassphrase = async (e: FormEvent) => {
    e.preventDefault();
    setEnrollPassBusy(true);
    setEnrollPassError(null);
    setEnrollPassNotice(null);
    try {
      const emailValue = enrollPassEmail.trim();
      try {
        await registerAccount(emailValue, enrollPassphrase);
        // An approved account has no password yet: register stores nothing and sends the
        // confirmation email, so there is nothing to sign in with. Do not attempt a sign-in
        // (it would 401 and feed the per-email lockout counter); point the person to the
        // email and stop a second submit from sending another one.
        setEnrollPassNotice(
          'Check your email for a confirmation link. Open it to choose your passphrase, then sign in.',
        );
        setEnrollPassSent(true);
        return;
      } catch (err) {
        // Link opened twice / account already set up → fall through to sign-in
        // with the entered passphrase. Duck-typed status (not instanceof) so
        // partially-mocked auth modules stay safe. Any non-409 error propagates.
        const status = (err as { status?: unknown } | null)?.status;
        if (status !== 409) throw err;
      }
      await login(emailValue, enrollPassphrase);
      // Land in the workspace. The ?return_to= query (already allowlist-validated
      // into externalReturnTo) routes an external vertical onward via the explicit
      // tenant-selection path; otherwise the S+ dashboard — the same onward route
      // as any other sign-in.
      await goAfterLogin();
    } catch (err) {
      setEnrollPassError(
        err instanceof Error ? err.message : 'Could not set your passphrase. Please try again.',
      );
    } finally {
      setEnrollPassBusy(false);
    }
  };

  // Post-login (bare) interstitial: the user is signed in and admitted to one or
  // more customer verticals, but not to a single unambiguous one (or has more
  // than one membership), so we offer an explicit, unmissable "Continue to X"
  // rather than redirecting or stranding them on the platform dashboard.
  if (postLoginLanding && postLoginLanding.verticals.length > 0) {
    return (
      <div className="slp">
        <style>{SLP_CSS}</style>
        <div className="slp-grain" aria-hidden="true" />
        <div className="slp-card">
          <div className="slp-brand">
            <span className="slp-brand-mark">S+</span>
            <span className="slp-brand-name">Seitiate+</span>
          </div>
          <div className="slp-heading">You're signed in</div>
          <div className="slp-msg slp-msg--note" style={{ marginTop: 0, marginBottom: 20 }}>
            Continue to your workspace.
          </div>
          {postLoginLanding.verticals.map((v, i) => (
            <button
              key={v.vertical_id}
              type="button"
              className={i === 0 ? 'slp-btn-primary' : 'slp-btn-ghost'}
              style={{ marginBottom: 12 }}
              onClick={() => window.location.assign(v.customer_home_url)}
            >
              Continue to {v.display_name} →
            </button>
          ))}
          <LinkRow
            links={[
              <button
                key="dashboard"
                type="button"
                className="slp-link"
                onClick={() => navigate('/dashboard', { replace: true })}
              >
                Go to the Seitiate+ dashboard
              </button>,
            ]}
          />
          <div className="slp-foot">{FOOTNOTE}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="slp">
      <style>{SLP_CSS}</style>
      <div className="slp-grain" aria-hidden="true" />
      <div className="slp-card">
        <div className="slp-brand">
          <span className="slp-brand-mark">S+</span>
          <span className="slp-brand-name">Seitiate+</span>
        </div>
        {isExternalCustomerLogin && mode === 'login' && (
          <PlatformBanner verticalId={externalLoginVertical(externalReturnTo)} returnTo={externalReturnTo} />
        )}
        {isExternalCustomerLogin && mode === 'login' && (
          <div className="slp-msg slp-msg--note" style={{ marginTop: 0, marginBottom: 16 }}>
            {operatorPasswordMode ? (
              <>Operator recovery signs in to Seitiate+ administration, not the customer workspace.</>
            ) : (
              <>
                Returning customers can sign in with their email and password or use a passkey.
              </>
            )}
          </div>
        )}

        {mode === 'enroll' ? (
          <div>
            <div className="slp-heading">Set up your access</div>
            <div className="slp-msg slp-msg--note" style={{ marginTop: 0, marginBottom: 20 }}>
              Your access is approved. Choose a passphrase to sign in — it's yours, works on
              any device, and you keep a recovery key so you're never locked out. Prefer a
              passkey on this device? That's optional, below.
            </div>

            {/* PRIMARY door: choose a passphrase (the shipped per-user credential). */}
            <form onSubmit={onEnrollPassphrase}>
              <label className="slp-label" htmlFor="enroll-email">
                Email
              </label>
              <input
                id="enroll-email"
                className="slp-input"
                type="email"
                autoComplete="username"
                name="seitiate-plus-enroll-email"
                value={enrollPassEmail}
                onChange={(e) => setEnrollPassEmail(e.target.value)}
                required
              />
              <div style={{ height: 16 }} />
              <label className="slp-label" htmlFor="enroll-passphrase">
                Passphrase
              </label>
              <div className="slp-field">
                <input
                  id="enroll-passphrase"
                  className="slp-input slp-input--password"
                  type={enrollPassShow ? 'text' : 'password'}
                  autoComplete="new-password"
                  name="seitiate-plus-enroll-passphrase"
                  value={enrollPassphrase}
                  onChange={(e) => setEnrollPassphrase(e.target.value)}
                  disabled={enrollPassSent}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="slp-toggle"
                  aria-label={enrollPassShow ? 'Hide passphrase' : 'Show passphrase'}
                  onClick={() => setEnrollPassShow((visible) => !visible)}
                >
                  {enrollPassShow ? 'HIDE' : 'SHOW'}
                </button>
              </div>
              <div className="slp-hint">At least 8 characters. You'll keep a recovery key.</div>
              {enrollPassError && <div className="slp-msg slp-msg--error">{enrollPassError}</div>}
              {enrollPassNotice && <div className="slp-msg slp-msg--note">{enrollPassNotice}</div>}
              <button
                type="submit"
                className="slp-btn-primary"
                disabled={enrollPassBusy || enrollPassSent}
                style={{ marginTop: 20 }}
              >
                {enrollPassBusy ? 'Setting up…' : 'Choose your passphrase'}
              </button>
            </form>

            {/* SECONDARY door: passkey — optional device convenience. */}
            {isPasskeySupported() && (
              <>
                <div className="slp-divider">
                  <span>or</span>
                </div>
                <div className="slp-msg slp-msg--note" style={{ marginTop: 0, marginBottom: 12 }}>
                  Prefer a passkey? Optional — a private key held on this device (Face ID, Touch
                  ID, or a security key). You may be prompted twice: once to create it, once to
                  sign in.
                </div>
                <button
                  type="button"
                  className="slp-btn-ghost"
                  onClick={onEnroll}
                  disabled={enrollBusy}
                >
                  {enrollBusy
                    ? enrollDone
                      ? 'Signing you in…'
                      : 'Setting up…'
                    : 'Set up your passkey'}
                </button>
                {enrollError && <div className="slp-msg slp-msg--error">{enrollError}</div>}
              </>
            )}

            <LinkRow
              links={[
                <button
                  key="recover"
                  type="button"
                  className="slp-link"
                  onClick={() => {
                    openRecover();
                    setRecoverEmail(enrollPassEmail);
                  }}
                >
                  Forgot your passphrase? Recover with your recovery key
                </button>,
                <button
                  key="have-account"
                  type="button"
                  className="slp-link"
                  onClick={() => {
                    setEnrollError(null);
                    setEnrollPassError(null);
                    setMode('login');
                  }}
                >
                  Already have an account? Sign in
                </button>,
              ]}
            />
            <div className="slp-foot">{FOOTNOTE}</div>
          </div>
        ) : mode === 'verify' ? (
          <div>
            <div className="slp-heading">Verify your email</div>
            {verifyBusy && (
              <div className="slp-msg slp-msg--note" style={{ marginTop: 0 }}>
                Verifying…
              </div>
            )}
            {!verifyBusy && verifyDone && (
              <div className="slp-msg slp-msg--note" style={{ marginTop: 0 }}>
                Your email is verified. You can sign in now.
              </div>
            )}
            {!verifyBusy && !verifyDone && verifyError && (
              <div className="slp-msg slp-msg--error">{verifyError}</div>
            )}
            {!verifyBusy && !verifyDone && verifyNeedsPassphrase && (
              <form onSubmit={onVerifyPassphrase}>
                <label className="slp-label" htmlFor="verify-passphrase">
                  Choose your passphrase
                </label>
                <input
                  id="verify-passphrase"
                  className="slp-input slp-input--password"
                  type="password"
                  autoComplete="new-password"
                  name="seitiate-plus-verify-passphrase"
                  value={verifyPassphrase}
                  onChange={(e) => setVerifyPassphrase(e.target.value)}
                  required
                  minLength={8}
                />
                <div className="slp-hint">At least 8 characters.</div>
                {verifyPassError && <div className="slp-msg slp-msg--error">{verifyPassError}</div>}
                <button
                  type="submit"
                  className="slp-btn-primary"
                  disabled={verifyPassBusy}
                  style={{ marginTop: 20 }}
                >
                  {verifyPassBusy ? 'Setting up…' : 'Set passphrase'}
                </button>
              </form>
            )}
            <LinkRow
              links={[
                <button key="back" type="button" className="slp-link" onClick={() => setMode('login')}>
                  Back to sign in
                </button>,
              ]}
            />
            <div className="slp-foot">{FOOTNOTE}</div>
          </div>
        ) : mode === 'register' ? (
          registerDone ? (
            <div>
              <div className="slp-heading">Account created</div>
              <div className="slp-msg slp-msg--note" style={{ marginTop: 0, marginBottom: 20 }}>
                Check your email to verify {registerEmail}. You don't need to verify before signing
                in — you're already good to go.
              </div>
              <button
                type="button"
                className="slp-btn-primary"
                onClick={onContinueAfterRegister}
                disabled={continueAfterRegisterBusy}
              >
                {continueAfterRegisterBusy ? 'Signing you in…' : 'Continue'}
              </button>
              <div className="slp-foot">{FOOTNOTE}</div>
            </div>
          ) : (
            <form onSubmit={onRegisterSubmit}>
              <div className="slp-heading">Create your account</div>
              <label className="slp-label" htmlFor="register-name">
                Name (optional)
              </label>
              <input
                id="register-name"
                className="slp-input"
                type="text"
                autoComplete="name"
                value={registerName}
                onChange={(e) => setRegisterName(e.target.value)}
              />
              <div style={{ height: 16 }} />
              <label className="slp-label" htmlFor="register-email">
                Email
              </label>
              <input
                id="register-email"
                className="slp-input"
                type="email"
                autoComplete="username"
                value={registerEmail}
                onChange={(e) => setRegisterEmail(e.target.value)}
                required
              />
              <div style={{ height: 16 }} />
              <label className="slp-label" htmlFor="register-password">
                Password
              </label>
              <div className="slp-field">
                <input
                  id="register-password"
                  className="slp-input slp-input--password"
                  type={registerShowPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={registerPassword}
                  onChange={(e) => setRegisterPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="slp-toggle"
                  aria-label={registerShowPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setRegisterShowPassword((visible) => !visible)}
                >
                  {registerShowPassword ? 'HIDE' : 'SHOW'}
                </button>
              </div>
              <div className="slp-hint">At least 8 characters.</div>
              {registerError && <div className="slp-msg slp-msg--error">{registerError}</div>}
              <button
                type="submit"
                className="slp-btn-primary"
                disabled={registerBusy}
                style={{ marginTop: 20 }}
              >
                {registerBusy ? 'Creating account...' : 'Create account'}
              </button>
              <LinkRow
                links={[
                  <button key="have-account" type="button" className="slp-link" onClick={backToLoginFromRegister}>
                    Already have an account? Sign in
                  </button>,
                ]}
              />
            </form>
          )
        ) : mode === 'forgot' ? (
          <form onSubmit={onForgotSubmit}>
            <div className="slp-msg slp-msg--note" style={{ marginTop: 0, marginBottom: 16 }}>
              Enter your email and, if it has an account, we'll send a code to reset the password.
            </div>
            <label className="slp-label" htmlFor="forgot-email">
              Email
            </label>
            <input
              id="forgot-email"
              className="slp-input"
              type="email"
              autoComplete="username"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              required
            />
            {forgotError && <div className="slp-msg slp-msg--error">{forgotError}</div>}
            {forgotNotice && <div className="slp-msg slp-msg--note">{forgotNotice}</div>}
            <button
              type="submit"
              className="slp-btn-primary"
              disabled={forgotBusy}
              style={{ marginTop: 20 }}
            >
              {forgotBusy ? 'Sending...' : 'Send reset code'}
            </button>
            <LinkRow
              links={[
                <button key="have-code" type="button" className="slp-link" onClick={openReset}>
                  Already have a reset code? Enter it
                </button>,
                <button key="back" type="button" className="slp-link" onClick={backToLoginFromForgot}>
                  Back to sign in
                </button>,
              ]}
            />
          </form>
        ) : mode === 'reset' ? (
          <form onSubmit={onResetSubmit}>
            <div className="slp-msg slp-msg--note" style={{ marginTop: 0, marginBottom: 16 }}>
              Enter the reset code we emailed you, along with your new password.
            </div>
            <label className="slp-label" htmlFor="reset-token">
              Reset code
            </label>
            <input
              id="reset-token"
              className="slp-input"
              type="text"
              autoComplete="off"
              value={resetToken}
              onChange={(e) => setResetToken(e.target.value)}
              required
            />
            <div style={{ height: 16 }} />
            <label className="slp-label" htmlFor="reset-new-password">
              New password
            </label>
            <div className="slp-field">
              <input
                id="reset-new-password"
                className="slp-input slp-input--password"
                type={resetShowPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={resetNewPassword}
                onChange={(e) => setResetNewPassword(e.target.value)}
                required
                minLength={8}
              />
              <button
                type="button"
                className="slp-toggle"
                aria-label={resetShowPassword ? 'Hide password' : 'Show password'}
                onClick={() => setResetShowPassword((visible) => !visible)}
              >
                {resetShowPassword ? 'HIDE' : 'SHOW'}
              </button>
            </div>
            {resetError && <div className="slp-msg slp-msg--error">{resetError}</div>}
            <button
              type="submit"
              className="slp-btn-primary"
              disabled={resetBusy}
              style={{ marginTop: 20 }}
            >
              {resetBusy ? 'Resetting...' : 'Reset password'}
            </button>
            <LinkRow
              links={[
                <button key="back" type="button" className="slp-link" onClick={backToLoginFromReset}>
                  Back to sign in
                </button>,
              ]}
            />
          </form>
        ) : mode === 'login' ? (
          <form onSubmit={onSubmit}>
            <div className="slp-section-label">Sign in</div>
            <label className="slp-label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="slp-input"
              type="email"
              autoComplete="username"
              name="seitiate-plus-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <div style={{ height: 16 }} />
            <label className="slp-label" htmlFor="password">
              Password
            </label>
            <div className="slp-field">
              <input
                id="password"
                className={`slp-input slp-input--password${error ? ' slp-input--error' : ''}`}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                name="seitiate-plus-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="slp-toggle"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((visible) => !visible)}
              >
                {showPassword ? 'HIDE' : 'SHOW'}
              </button>
            </div>
            {operatorPasswordMode && (
              <>
                <div style={{ height: 16 }} />
                <label className="slp-label" htmlFor="director-key">
                  Director key
                </label>
                <input
                  id="director-key"
                  className="slp-input"
                  type="password"
                  autoComplete="off"
                  name="seitiate-plus-director-key"
                  value={directorKey}
                  onChange={(e) => setDirectorKey(e.target.value)}
                />
                <div className="slp-hint">
                  Required on deployed engines; leave empty on a local engine.
                </div>
              </>
            )}
            {error && <div className="slp-msg slp-msg--error">{error}</div>}
            {!error && routeMessage && <div className="slp-msg slp-msg--note">{routeMessage}</div>}
            {!error && !routeMessage && notice && (
              <div className="slp-msg slp-msg--note">{notice}</div>
            )}
            <button
              type="submit"
              className="slp-btn-primary"
              disabled={busy}
              style={{ marginTop: 20 }}
            >
              {busy ? 'Signing in...' : passwordLoginLabel}
            </button>
            {isPasskeySupported() && (
              <>
                <div className="slp-divider">
                  <span>or</span>
                </div>
                <button
                  type="button"
                  className="slp-btn-ghost"
                  onClick={onPasskeyLogin}
                  disabled={busy}
                >
                  {busy ? 'Signing in...' : 'Sign in with your Seitiate Passkey'}
                </button>
              </>
            )}
            <LinkRow
              links={[
                <button key="forgot-pw" type="button" className="slp-link" onClick={openForgotPassword}>
                  Forgot your password? Reset it by email
                </button>,
                !isExternalCustomerLogin && (
                  <button key="register" type="button" className="slp-link" onClick={openRegister}>
                    New here? Create an account
                  </button>
                ),
                <button key="switch" type="button" className="slp-link" onClick={switchAccount}>
                  Use a different account
                </button>,
                <button key="recover" type="button" className="slp-link" onClick={openRecover}>
                  Forgot your passphrase? Recover with your recovery key
                </button>,
                isExternalCustomerLogin && !operatorPasswordMode && (
                  <button
                    key="operator"
                    type="button"
                    className="slp-link"
                    onClick={() => setOperatorPasswordMode(true)}
                  >
                    Operator password override
                  </button>
                ),
                isExternalCustomerLogin && operatorPasswordMode && (
                  <button
                    key="operator-back"
                    type="button"
                    className="slp-link"
                    onClick={() => {
                      setOperatorPasswordMode(false);
                      setPassword('');
                      setDirectorKey('');
                      setShowPassword(false);
                    }}
                  >
                    Back to customer sign-in
                  </button>
                ),
              ]}
            />
            {isExternalCustomerLogin && (
              <div className="slp-foot">
                On a shared computer, or the passkey prompt keeps offering the wrong account? Open
                the access link from your email to set up your own passkey, or use a different
                account to clear this device first.
              </div>
            )}
            <div className="slp-foot">{FOOTNOTE}</div>
          </form>
        ) : (
          <form onSubmit={onRecoverSubmit}>
            <div className="slp-msg slp-msg--note" style={{ marginTop: 0, marginBottom: 16 }}>
              Locked out? Reset your seity passphrase with the recovery key you saved when you first
              set it. You do not need to be signed in.
            </div>
            <label className="slp-label" htmlFor="recover-email">
              Email
            </label>
            <input
              id="recover-email"
              className="slp-input"
              type="email"
              autoComplete="username"
              name="seitiate-plus-recover-email"
              value={recoverEmail}
              onChange={(e) => setRecoverEmail(e.target.value)}
              required
            />
            <div style={{ height: 16 }} />
            <label className="slp-label" htmlFor="recover-key">
              Recovery key
            </label>
            <input
              id="recover-key"
              className="slp-input"
              type="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              name="seitiate-plus-recovery-key"
              placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
              value={recoverKey}
              onChange={(e) => setRecoverKey(e.target.value)}
              required
            />
            <div style={{ height: 16 }} />
            <label className="slp-label" htmlFor="recover-new-pass">
              New passphrase
            </label>
            <input
              id="recover-new-pass"
              className="slp-input"
              type="password"
              autoComplete="new-password"
              name="seitiate-plus-new-passphrase"
              value={recoverNewPass}
              onChange={(e) => setRecoverNewPass(e.target.value)}
              required
            />
            {recoverError && <div className="slp-msg slp-msg--error">{recoverError}</div>}
            {recoverNotice && <div className="slp-msg slp-msg--note">{recoverNotice}</div>}
            <button
              type="submit"
              className="slp-btn-primary"
              disabled={recoverBusy}
              style={{ marginTop: 20 }}
            >
              {recoverBusy ? 'Resetting...' : 'Reset passphrase'}
            </button>
            <LinkRow
              links={[
                <button key="back" type="button" className="slp-link" onClick={backToLogin}>
                  Back to sign in
                </button>,
              ]}
            />
          </form>
        )}
      </div>
    </div>
  );
}
